import React, { useState, useEffect } from 'react';
import { Wallet, PlusCircle, Receipt, ArrowDownCircle, ArrowUpCircle, Camera, Download, X } from 'lucide-react';
import api from '../api/axiosInstance';

const PettyCash = () => {
    const [logs, setLogs] = useState([]);
    const [balance, setBalance] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [projects, setProjects] = useState([]);
    const [formData, setFormData] = useState({ amount: '', type: 'debit', category: 'hardware', description: '', project_id: '', bill_photo: null });

    useEffect(() => {
        fetchLogs();
        api.get('get_projects.php').then(res => setProjects(res.data.projects));
    }, []);

    const fetchLogs = async () => {
        const res = await api.get('get_petty_cash.php');
        setLogs(res.data.logs);
        // Calculate balance: Credits - Debits
        const total = res.data.logs.reduce((acc, log) =>
            log.type === 'credit' ? acc + parseFloat(log.amount) : acc - parseFloat(log.amount), 0);
        setBalance(total);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation before sending
        if (!formData.amount) return alert("Please enter an amount");

        const data = new FormData();
        data.append('amount', formData.amount);
        data.append('type', formData.type || 'debit');
        data.append('category', formData.category);
        data.append('description', formData.description);
        data.append('project_id', formData.project_id);

        if (formData.bill_photo) {
            data.append('bill_photo', formData.bill_photo);
        }

        try {
            const res = await api.post('save_petty_cash.php', data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.status === 'success') {
                setIsModalOpen(false);
                fetchLogs(); // Refresh list
                // Reset form
                setFormData({ amount: '', type: 'debit', category: 'hardware', description: '', project_id: '', bill_photo: null });
            } else {
                alert(res.data.message);
            }
        } catch (err) {
            alert("Server Error while saving expense");
        }
    };

    return (
        <div className="space-y-8 p-4">
            {/* Wallet Header */}
            <div className="bg-slate-900 rounded-[40px] p-10 text-white shadow-2xl relative overflow-hidden">
                <div className="relative z-10">
                    <p className="text-indigo-400 font-black text-xs uppercase tracking-widest mb-2">Available Site Cash</p>
                    <h2 className="text-6xl font-black italic">₹{balance.toLocaleString()}</h2>
                    <button onClick={() => setIsModalOpen(true)} className="mt-8 bg-indigo-600 hover:bg-indigo-500 px-8 py-4 rounded-2xl font-black flex items-center gap-2 transition-all">
                        <PlusCircle size={20} /> Log Expense
                    </button>
                </div>
                <Wallet size={200} className="absolute -right-10 -bottom-10 text-white/5 rotate-12" />
            </div>

            {/* Transaction History */}
            <div className="space-y-4">
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-4">Recent Transactions</h3>
                {logs.map(log => (
                    <div key={log.id} className="bg-white p-6 rounded-[32px] border border-slate-100 flex items-center justify-between group hover:shadow-lg transition-all">
                        <div className="flex items-center gap-6">
                            <div className={`p-4 rounded-2xl ${log.type === 'credit' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                                {log.type === 'credit' ? <ArrowDownCircle /> : <ArrowUpCircle />}
                            </div>
                            <div>
                                <h4 className="font-black text-slate-900 italic uppercase text-sm">{log.description || log.category}</h4>
                                <p className="text-[10px] font-bold text-slate-400">{log.created_at} • {log.project_name || 'General'}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className={`text-xl font-black ${log.type === 'credit' ? 'text-emerald-600' : 'text-slate-900'}`}>
                                {log.type === 'credit' ? '+' : '-'}₹{parseFloat(log.amount).toLocaleString()}
                            </p>
                            {log.bill_image && (
                                <a href={`https://backendibos.laxmaninterior.in/${log.bill_image}`} target="_blank" className="text-[9px] font-black text-indigo-600 uppercase hover:underline">View Receipt</a>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* EXPENSE MODAL */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[300] bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-6">
                    <form onSubmit={handleSubmit} className="bg-white w-full max-w-xl rounded-[48px] p-10 space-y-6 shadow-2xl">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-black italic tracking-tighter">Site Expense Log</h2>
                            <button type="button" onClick={() => setIsModalOpen(false)}><X /></button>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Amount (₹)</label>
                                <input required type="number" className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none border-2 border-transparent focus:border-indigo-600" onChange={e => setFormData({ ...formData, amount: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Category</label>
                                <select className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none" onChange={e => setFormData({ ...formData, category: e.target.value })}>
                                    <option value="hardware">Hardware</option>
                                    <option value="transport">Transport</option>
                                    <option value="labor_tea">Labor/Tea</option>
                                    <option value="material">Material</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                        </div>

                        <select className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none" onChange={e => setFormData({ ...formData, project_id: e.target.value })}>
                            <option value="">Select Project Site...</option>
                            {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                        </select>

                        <input placeholder="What was this for? (e.g. 2kg Screws)" className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none" onChange={e => setFormData({ ...formData, description: e.target.value })} />

                        <div className="relative h-20 border-2 border-dashed border-slate-200 rounded-2xl flex items-center justify-center bg-slate-50 overflow-hidden">
                            {formData.bill_photo ? <span className="text-xs font-black text-emerald-600">Photo Attached ✓</span> : <><Camera size={20} className="mr-2 text-slate-400" /> <span className="text-xs font-bold text-slate-400">Capture Bill Receipt</span></>}
                            <input type="file" capture="environment" className="absolute inset-0 opacity-0" onChange={e => setFormData({ ...formData, bill_photo: e.target.files[0] })} />
                        </div>

                        <button className="w-full py-5 bg-indigo-600 text-white rounded-3xl font-black text-lg shadow-xl hover:bg-indigo-700 transition-all">Save Transaction</button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default PettyCash;